"use client";

import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import { ComponentPropsWithoutRef, FC, ReactElement } from "react";
import { useMessages } from "../context/i18n";
import { rivalPage as enRivalPage } from "../dictionaries/en/rival-page";
import Button from "./Button";
import Logo from "./Logo";
import Typography from "./Typography";

type AlternativeCompareTableSectionProps = {
  comparee: string;
  compareItems: CompareItem[];
  // Override the small caption rendered beneath the table. The default
  // dates only the competitor's column: Klap's rows (prices, trial) are
  // rendered from const/publicPricing.ts, so they're always current, while
  // peer pages' (veed-io, submagic, …) competitor data was last checked in
  // February 2025.
  updatedLabel?: string;
  // The rival's own pages behind its column, linked under the table.
  sources?: AlternativeSource[];
} & ComponentPropsWithoutRef<"section">;

export type AlternativeSource = {
  label: string;
  href: string;
};

export type CompareItem = {
  name: string;
  us: boolean | string;
  them: boolean | string;
};

const AlternativeCompareTableSection: FC<
  AlternativeCompareTableSectionProps
> = ({ comparee, compareItems, updatedLabel, sources, ...rest }) => {
  const m = useMessages("rivalPage", enRivalPage);
  return (
    <section {...rest} className={`${rest.className}`}>
      <Typography variant="h2" className="text-center mt-4">
        {m.comparing}{" "}
        <strong className="font-semibold text-violet-600">
          {m.versus.replace("{name}", comparee)}
        </strong>
      </Typography>
      <Link href="/login">
        <Button
          size="l"
          variant="primary"
          className="mt-8 mx-auto block"
          onClick={() => {}}
        >
          {m.tryKlap}
        </Button>
      </Link>
      <div className="overflow-x-auto mt-12">
        <div className=" overflow-hidden border-slate-200 border rounded-2xl">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="border-b border-slate-200 grid grid-cols-3">
                <th className="p-3"></th>
                <th className="p-3 flex items-center justify-center">
                  <span className="sr-only">Klap</span>
                  <Logo />
                </th>
                <th className="p-3">
                  <Typography variant="h4">{comparee}</Typography>
                </th>
              </tr>
            </thead>
            <tbody>
              {compareItems.map((feature, idx) => (
                <tr key={idx} className="even:bg-slate-50 grid grid-cols-3">
                  <td className="p-4 text-slate-600">{feature.name}</td>
                  <td className="p-4 font-semibold text-center">
                    {getValueRepresentation(feature.us, m)}
                  </td>
                  <td className="p-4 font-semibold text-center">
                    {getValueRepresentation(feature.them, m)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Typography
          variant="caption"
          className="mx-auto text-slate-600 text-center mt-4"
        >
          {updatedLabel ??
            `Klap: current plans. ${comparee}: as of February 2025.`}
        </Typography>
        {sources && sources.length > 0 && (
          <Typography
            variant="caption"
            className="mx-auto text-slate-600 text-center mt-1"
          >
            {m.sources.replace("{name}", comparee)}{" "}
            {sources.map((source, idx) => (
              <span key={source.href}>
                {idx > 0 && ", "}
                <a
                  className="underline underline-offset-2"
                  href={source.href}
                  rel="nofollow noopener"
                  target="_blank"
                >
                  {source.label}
                </a>
              </span>
            ))}
          </Typography>
        )}
      </div>
    </section>
  );
};

function getValueRepresentation(
  value: boolean | string,
  m: Pick<typeof enRivalPage, "yes" | "no">,
): ReactElement | string {
  if (typeof value === "boolean") {
    // Heroicons outline icons render at stroke-width 1.5 by default; bumping
    // to 2.25 reads visibly bolder against the table rows without losing the
    // outline silhouette (a solid swap would dominate the layout).
    // The sr-only word gives screen readers and crawlers an answer, since
    // the icon alone leaves the cell empty in the HTML.
    return value ? (
      <>
        <span className="sr-only">{m.yes}</span>
        <CheckIcon
          strokeWidth={2.25}
          className="text-green-500 w-6 h-6 mx-auto"
        />
      </>
    ) : (
      <>
        <span className="sr-only">{m.no}</span>
        <XMarkIcon
          strokeWidth={2.25}
          className="text-red-500 w-6 h-6 mx-auto"
        />
      </>
    );
  }
  return value;
}

export default AlternativeCompareTableSection;
